import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSettings } from '../../context/SettingsContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Plus, Eye, UserPlus, UserCog, Trash2, Power, Users, Shield, MonitorSmartphone, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClients({ inWorkspace, onClientClick }) {
  const { token, register, user, enterMirrorMode } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['super_admin', 'admin_ops', 'bureau'].includes(user?.role);
  const { t } = useLanguage();
  const { platformSettings: pSettings } = useSettings();
  const [clients, setClients] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(sessionStorage.getItem('clientsSearch') || '');
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || sessionStorage.getItem('clientsStatusFilter') || 'all');
  const [officeFilter, setOfficeFilter] = useState(sessionStorage.getItem('clientsOfficeFilter') || 'all');
  const [analystFilter, setAnalystFilter] = useState(sessionStorage.getItem('clientsAnalystFilter') || 'all');
  const [onlineClientIds, setOnlineClientIds] = useState(new Set());
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ first_name: '', last_name: '', email: '', password: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analystDialogOpen, setAnalystDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'client',
    assigned_analyst_id: ''
  });
  const [newAnalyst, setNewAnalyst] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'analyst'
  });

  // WhatsApp templates state
  const [waTemplates, setWaTemplates] = useState([]);
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [waClient, setWaClient] = useState(null);
  const [waCustomFields, setWaCustomFields] = useState({});

  const fetchWaTemplates = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp-templates`, { headers: { Authorization: `Bearer ${token}` } });
      setWaTemplates(res.data || []);
    } catch {}
  };

  const openWhatsApp = (client) => {
    setWaClient(client);
    setWaCustomFields({});
    setWaModalOpen(true);
    if (waTemplates.length === 0) fetchWaTemplates();
  };

  const getPlatformName = () => {
    return pSettings?.platform_name || 'La Plateforme';
  };

  const sendWhatsApp = (template) => {
    if (!waClient) return;
    const clientLastName = (waClient.last_name || waClient.user?.last_name || '').toUpperCase();
    const analystName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    const signature = `${analystName} - ${getPlatformName()}`;
    const phone = (waClient.phone_primary || waClient.phone_secondary || waClient.user?.phone || '').replace(/[^0-9+]/g, '');

    let msg = template.body
      .replace(/\{NOM\}/g, clientLastName)
      .replace(/\{SIGNATURE\}/g, signature)
      .replace(/\{DATE\}/g, waCustomFields.date || '______')
      .replace(/\{HEURE\}/g, waCustomFields.heure || '______');

    const encoded = encodeURIComponent(msg);
    const cleanPhone = phone.startsWith('+') ? phone.slice(1) : phone.startsWith('0') ? '33' + phone.slice(1) : phone;
    window.open(`https://wa.me/${cleanPhone}?text=${encoded}`, '_blank');
    setWaModalOpen(false);
  };

  useEffect(() => {
    fetchData();
    fetchWaTemplates();
  }, [token]);

  // Persist filters
  useEffect(() => {
    sessionStorage.setItem('clientsSearch', search);
    sessionStorage.setItem('clientsStatusFilter', statusFilter);
    sessionStorage.setItem('clientsOfficeFilter', officeFilter);
    sessionStorage.setItem('clientsAnalystFilter', analystFilter);
  }, [search, statusFilter, officeFilter, analystFilter]);

  const fetchData = async () => {
    try {
      const [clientsRes, usersRes, officesRes, liveRes] = await Promise.all([
        axios.get(`${API}/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/users?role=analyst`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/offices`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/tracking/clients-live`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { clients: [] } }))
      ]);
      setClients(clientsRes.data);
      setAnalysts(usersRes.data);
      setOffices(Array.isArray(officesRes.data) ? officesRes.data : []);
      setOnlineClientIds(new Set((liveRes.data?.clients || []).map(c => c.id)));
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error(t('error'), { description: 'Erreur lors du chargement des clients' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      const result = await register(newClient);
      if (!result.success) {
        toast.error(t('error'), { description: result.error || 'Erreur lors de la création' });
        return;
      }
      toast.success(t('success'), { description: 'Client créé avec succès' });
      setDialogOpen(false);
      setNewClient({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'client',
        assigned_analyst_id: '',
        account_type: 'particulier',
        company_name: '',
        company_siret: '',
        company_legal_form: '',
        company_address: '',
        company_city: '',
        company_postal_code: '',
        company_activity: '',
        is_test: false,
      });
      fetchData();
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Erreur lors de la création' });
    }
  };

  const handleCreateAnalyst = async () => {
    try {
      await axios.post(`${API}/auth/register`, newAnalyst, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('success'), { description: 'Analyste créé avec succès' });
      setAnalystDialogOpen(false);
      setNewAnalyst({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'analyst'
      });
      fetchData();
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Erreur lors de la création' });
    }
  };

  const handleDeleteClient = async (client) => {
    if (!isAdmin) return;
    const confirmDelete = window.confirm(`Supprimer le client ${client.user?.first_name || ''} ${client.user?.last_name || ''} ? Cette action est irréversible.`);
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/clients/${client.user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(prev => prev.filter(item => item.user_id !== client.user_id));
      toast.success(t('success'), { description: 'Client supprimé' });
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  // Filter without status first (for accurate counts)
  const baseFiltered = [...clients].sort((a, b) => {
    const dateA = a.user?.created_at || '';
    const dateB = b.user?.created_at || '';
    return dateB.localeCompare(dateA);
  }).filter(client => {
    const searchLower = search.toLowerCase();
    const searchNoSpaces = search.replace(/\s/g, '');
    const matchSearch = !search || (
      client.user?.first_name?.toLowerCase().includes(searchLower) ||
      client.user?.last_name?.toLowerCase().includes(searchLower) ||
      client.user?.email?.toLowerCase().includes(searchLower) ||
      client.client_code?.toLowerCase().includes(searchLower) ||
      (client.phone_primary || '').replace(/\s/g, '').includes(searchNoSpaces) ||
      (client.phone_secondary || '').replace(/\s/g, '').includes(searchNoSpaces) ||
      (client.user?.phone || '').replace(/\s/g, '').includes(searchNoSpaces) ||
      (client.company_name || '').toLowerCase().includes(searchLower) ||
      (client.company_siret || '').replace(/\s/g, '').includes(searchNoSpaces)
    );
    if (!matchSearch) return false;
    if (officeFilter !== 'all') {
      if (officeFilter === 'none') {
        const clientOffice = client.office_id;
        const analyst = analysts.find(a => a.id === client.assigned_analyst_id);
        if (clientOffice || analyst?.office_id) return false;
      } else {
        const clientOffice = client.office_id;
        const analyst = analysts.find(a => a.id === client.assigned_analyst_id);
        if (clientOffice !== officeFilter && analyst?.office_id !== officeFilter) return false;
      }
    }
    if (analystFilter !== 'all') {
      if (analystFilter === 'none') {
        if (client.assigned_analyst_id) return false;
      } else {
        if (client.assigned_analyst_id !== analystFilter) return false;
      }
    }
    return true;
  });

  // Then apply status filter
  const filteredClients = baseFiltered.filter(client => {
    if (statusFilter === 'active' && client.user?.status !== 'active') return false;
    if (statusFilter === 'inactive' && client.user?.status === 'active') return false;
    return true;
  });

  const activeCount = baseFiltered.filter(c => c.user?.status === 'active').length;
  const inactiveCount = baseFiltered.filter(c => c.user?.status !== 'active').length;

  const handleToggleStatus = async (client) => {
    const isActive = client.user?.status === 'active';
    const newStatus = isActive ? 'blocked' : 'active';
    const confirm = window.confirm(
      isActive 
        ? `Désactiver ${client.user?.first_name} ${client.user?.last_name} ? Le client ne pourra plus se connecter.`
        : `Réactiver ${client.user?.first_name} ${client.user?.last_name} ?`
    );
    if (!confirm) return;
    try {
      await axios.put(`${API}/clients/${client.user_id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isActive ? 'Client désactivé' : 'Client réactivé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'status-active',
      pending_first_login: 'status-pending',
      suspended: 'status-suspended',
      blocked: 'status-rejected'
    };
    const labels = {
      active: 'Actif',
      pending_first_login: 'En attente',
      suspended: 'Suspendu',
      blocked: 'Bloqué'
    };
    return (
      <Badge className={styles[status] || 'status-neutral'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const content = (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Rechercher un client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-card"
                data-testid="search-clients-input"
              />
            </div>
            {/* Status filter */}
            <div className="flex gap-1 bg-secondary/50 rounded-lg p-1">
              <Button size="sm" variant={statusFilter === 'all' ? 'default' : 'ghost'} onClick={() => setStatusFilter('all')} data-testid="filter-all">
                Tous <Badge variant="secondary" className="ml-1">{baseFiltered.length}</Badge>
              </Button>
              <Button size="sm" variant={statusFilter === 'active' ? 'default' : 'ghost'} onClick={() => setStatusFilter('active')} className={statusFilter === 'active' ? '' : 'text-green-500'} data-testid="filter-active">
                Actifs <Badge variant="secondary" className="ml-1">{activeCount}</Badge>
              </Button>
              <Button size="sm" variant={statusFilter === 'inactive' ? 'default' : 'ghost'} onClick={() => setStatusFilter('inactive')} className={statusFilter === 'inactive' ? '' : 'text-red-500'} data-testid="filter-inactive">
                Inactifs <Badge variant="secondary" className="ml-1">{inactiveCount}</Badge>
              </Button>
            </div>
          </div>
          
          {/* Bureau & Analyst filters - hide bureau filter for bureau role */}
          {(offices.length > 0 || analysts.length > 0) && (
            <div className="flex gap-3 flex-wrap">
              {offices.length > 0 && user?.role !== 'bureau' && (
                <select
                  className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={officeFilter}
                  onChange={(e) => setOfficeFilter(e.target.value)}
                  data-testid="filter-office"
                >
                  <option value="all">Tous les bureaux</option>
                  {offices.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                  <option value="none">Sans bureau</option>
                </select>
              )}
              {analysts.length > 0 && (
                <select
                  className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={analystFilter}
                  onChange={(e) => setAnalystFilter(e.target.value)}
                  data-testid="filter-analyst"
                >
                  <option value="all">Tous les gestionnaires</option>
                  {analysts.map(a => (
                    <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
                  ))}
                  <option value="none">Non assigne</option>
                </select>
              )}
            </div>
          )}

          <div className="flex gap-2">
            {/* Client Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-gold" data-testid="create-client-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nouveau client
                </Button>
              </DialogTrigger>
              <DialogContent className="glass sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Créer un nouveau client</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Account type selector */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setNewClient(prev => ({ ...prev, account_type: 'particulier' }))}
                      className={`flex-1 py-2 rounded text-sm font-semibold transition-all ${
                        (newClient.account_type || 'particulier') === 'particulier' 
                          ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                      }`}
                      data-testid="account-type-particulier"
                    >Particulier</button>
                    <button
                      onClick={() => setNewClient(prev => ({ ...prev, account_type: 'professionnel' }))}
                      className={`flex-1 py-2 rounded text-sm font-semibold transition-all ${
                        newClient.account_type === 'professionnel' 
                          ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                      }`}
                      data-testid="account-type-professionnel"
                    >Professionnel</button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom</Label>
                      <Input
                        value={newClient.first_name}
                        onChange={(e) => setNewClient(prev => ({ ...prev, first_name: e.target.value }))}
                        className="bg-card/50"
                        data-testid="new-client-firstname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom</Label>
                    <Input
                      value={newClient.last_name}
                      onChange={(e) => setNewClient(prev => ({ ...prev, last_name: e.target.value }))}
                      className="bg-card/50"
                      data-testid="new-client-lastname"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-card/50"
                    data-testid="new-client-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe provisoire</Label>
                  <Input
                    type="password"
                    value={newClient.password}
                    onChange={(e) => setNewClient(prev => ({ ...prev, password: e.target.value }))}
                    className="bg-card/50"
                    data-testid="new-client-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-card/50"
                    data-testid="new-client-phone"
                  />
                </div>
                {/* Company fields for professional accounts */}
                {newClient.account_type === 'professionnel' && (
                  <div className="space-y-3 p-3 rounded-lg border border-primary/20 bg-primary/5">
                    <p className="text-xs font-semibold text-primary">Informations société</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Raison sociale *</Label>
                        <Input value={newClient.company_name || ''} onChange={(e) => setNewClient(prev => ({ ...prev, company_name: e.target.value }))} className="bg-card/50 h-8 text-sm" placeholder="Ma Société SAS" data-testid="new-client-company-name" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">SIRET</Label>
                        <Input value={newClient.company_siret || ''} onChange={(e) => setNewClient(prev => ({ ...prev, company_siret: e.target.value }))} className="bg-card/50 h-8 text-sm" placeholder="123 456 789 00012" data-testid="new-client-company-siret" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Forme juridique</Label>
                        <Select value={newClient.company_legal_form || ''} onValueChange={(v) => setNewClient(prev => ({ ...prev, company_legal_form: v }))}>
                          <SelectTrigger className="bg-card/50 h-8 text-sm"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SAS">SAS</SelectItem>
                            <SelectItem value="SARL">SARL</SelectItem>
                            <SelectItem value="SA">SA</SelectItem>
                            <SelectItem value="SCI">SCI</SelectItem>
                            <SelectItem value="EURL">EURL</SelectItem>
                            <SelectItem value="SASU">SASU</SelectItem>
                            <SelectItem value="Auto-entrepreneur">Auto-entrepreneur</SelectItem>
                            <SelectItem value="Autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Activité</Label>
                        <Input value={newClient.company_activity || ''} onChange={(e) => setNewClient(prev => ({ ...prev, company_activity: e.target.value }))} className="bg-card/50 h-8 text-sm" placeholder="Conseil, Commerce..." />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Adresse société</Label>
                      <Input value={newClient.company_address || ''} onChange={(e) => setNewClient(prev => ({ ...prev, company_address: e.target.value }))} className="bg-card/50 h-8 text-sm" placeholder="12 rue de la Paix" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Ville</Label>
                        <Input value={newClient.company_city || ''} onChange={(e) => setNewClient(prev => ({ ...prev, company_city: e.target.value }))} className="bg-card/50 h-8 text-sm" placeholder="Paris" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Code postal</Label>
                        <Input value={newClient.company_postal_code || ''} onChange={(e) => setNewClient(prev => ({ ...prev, company_postal_code: e.target.value }))} className="bg-card/50 h-8 text-sm" placeholder="75001" />
                      </div>
                    </div>
                  </div>
                )}
                {/* Client Test checkbox */}
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                  <button
                    onClick={() => setNewClient(prev => ({ ...prev, is_test: !prev.is_test }))}
                    className={`w-5 h-5 rounded border flex items-center justify-center text-xs ${newClient.is_test ? 'bg-amber-500 border-amber-500 text-black' : 'border-muted-foreground'}`}
                    data-testid="new-client-is-test"
                  >{newClient.is_test ? '✓' : ''}</button>
                  <div>
                    <p className="text-sm font-semibold">Client Test</p>
                    <p className="text-[10px] text-muted-foreground">Les données de ce client seront exclues des statistiques et calculs</p>
                  </div>
                </div>
                {isAdmin && (
                  <div className="space-y-2">
                    <Label>Analyste assigné</Label>
                    <Select
                      value={newClient.assigned_analyst_id}
                      onValueChange={(value) => setNewClient(prev => ({ ...prev, assigned_analyst_id: value }))}
                    >
                      <SelectTrigger className="bg-card/50" data-testid="new-client-analyst">
                        <SelectValue placeholder="Sélectionner un analyste" />
                      </SelectTrigger>
                      <SelectContent>
                        {analysts.map(analyst => (
                          <SelectItem key={analyst.id} value={analyst.id}>
                            {analyst.first_name} {analyst.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button 
                  onClick={handleCreateClient} 
                  className="w-full btn-gold"
                  data-testid="confirm-create-client"
                >
                  Créer le client
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Clients Table */}
        <Card className="card-premium">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t('no_data')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-premium">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Tél</th>
                      <th>Créé le</th>
                      <th>Bureau</th>
                      <th>Gestionnaire</th>
                      <th>{t('status')}</th>
                      <th>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => (
                      <tr key={client.id} className={`cursor-pointer transition-colors ${onlineClientIds.has(client.user_id) ? 'bg-green-500/10 hover:bg-green-500/20 border-l-2 border-green-500' : 'hover:bg-muted/30'}`} onClick={() => onClientClick ? onClientClick(client.user_id, `${client.user?.first_name || ''} ${client.user?.last_name || ''}`.trim()) : navigate(`/admin/clients/${client.user_id}`)} data-testid={`client-row-${client.user_id}`}>
                        <td>
                          <span className="font-mono text-[#D4AF37]">{client.client_code}</span>
                        </td>
                        <td>
                          <span className="font-medium">
                            {client.user?.first_name} {client.user?.last_name}
                          </span>
                        </td>
                        <td className="text-muted-foreground">{client.user?.email}</td>
                        <td className="text-muted-foreground text-xs">{client.phone_primary || client.user?.phone || '—'}</td>
                        <td className="text-muted-foreground text-xs">{client.user?.created_at ? new Date(client.user.created_at).toLocaleDateString('fr-FR') : '—'}</td>
                        <td>
                          {(() => {
                            const office = offices.find(o => o.id === client.office_id) || 
                              (() => { const a = analysts.find(a => a.id === client.assigned_analyst_id); return a ? offices.find(o => o.id === a.office_id) : null; })();
                            return office ? (
                              <Badge variant="outline" className="text-xs">{office.name}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            );
                          })()}
                        </td>
                        <td>
                          {client.analyst ? (
                            <span className="text-sm">{client.analyst.first_name} {client.analyst.last_name}</span>
                          ) : (
                            <span className="text-muted-foreground text-xs">Non assigné</span>
                          )}
                        </td>
                        <td>{getStatusBadge(client.user?.status)}</td>
                        <td>
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(client)}
                              className={client.user?.status === 'active' ? 'text-green-500 hover:text-red-500' : 'text-red-500 hover:text-green-500'}
                              title={client.user?.status === 'active' ? 'Désactiver' : 'Réactiver'}
                              data-testid={`toggle-client-${client.user_id}`}
                            >
                              <Power className="w-4 h-4" />
                            </Button>
                            {onClientClick ? (
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onClientClick(client.user_id, `${client.user?.first_name || ''} ${client.user?.last_name || ''}`.trim()); }} data-testid={`view-client-${client.user_id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Link to={`/admin/clients/${client.user_id}`}>
                                <Button variant="ghost" size="sm" data-testid={`view-client-${client.user_id}`}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-500 hover:text-blue-600"
                              title="Mode miroir"
                              onClick={(e) => { 
                                e.stopPropagation();
                                // Store mirror request in localStorage (shared between tabs)
                                localStorage.setItem('pending_mirror', JSON.stringify({ id: client.user_id, ts: Date.now() }));
                                window.open(`${window.location.origin}/client/dashboard`, '_blank');
                              }}
                              data-testid={`mirror-client-${client.user_id}`}
                            >
                              <MonitorSmartphone className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-500 hover:text-green-600"
                              title="WhatsApp"
                              onClick={(e) => { e.stopPropagation(); openWhatsApp(client); }}
                              data-testid={`whatsapp-client-${client.user_id}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClient(client)}
                                data-testid={`delete-client-${client.user_id}`}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* WhatsApp Template Modal */}
      <Dialog open={waModalOpen} onOpenChange={setWaModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              WhatsApp — {waClient?.first_name || waClient?.user?.first_name} {waClient?.last_name || waClient?.user?.last_name}
            </DialogTitle>
            <DialogDescription>Sélectionnez un template de message</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto py-2">
            {waTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun template disponible</p>
            ) : waTemplates.map((tpl) => {
              const needsDate = tpl.body.includes('{DATE}');
              const needsHeure = tpl.body.includes('{HEURE}');
              return (
                <div key={tpl.id} className="p-3 rounded-lg border hover:bg-secondary/30 transition-colors" data-testid={`wa-template-${tpl.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">{tpl.title}</p>
                    {tpl.is_default && <Badge variant="outline" className="text-[10px]">Par défaut</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-pre-line mb-2 line-clamp-3">{tpl.body.replace(/\{NOM\}/g, (waClient?.last_name || waClient?.user?.last_name || '').toUpperCase()).replace(/\{SIGNATURE\}/g, `${user?.first_name || ''} ${user?.last_name || ''} - ...`).slice(0, 150)}...</p>
                  {(needsDate || needsHeure) && (
                    <div className="flex items-center gap-2 mb-2">
                      {needsDate && <Input type="date" className="h-8 text-xs w-36" value={waCustomFields.date || ''} onChange={(e) => setWaCustomFields(p => ({...p, date: e.target.value}))} placeholder="Date" />}
                      {needsHeure && <Input type="time" className="h-8 text-xs w-28" value={waCustomFields.heure || ''} onChange={(e) => setWaCustomFields(p => ({...p, heure: e.target.value}))} placeholder="Heure" />}
                    </div>
                  )}
                  <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={() => sendWhatsApp(tpl)} data-testid={`wa-send-${tpl.id}`}>
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Envoyer
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  return inWorkspace ? content : <DashboardLayout>{content}</DashboardLayout>;
}
